import EyeIcons from '@/icons/EyeIcons'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface MetadataProps {
    metadata: any
}

const UnpickKey = [
    'deleted_at',
    'created_at',
    'updated_at',
    'id',
    'service_template',
    'service_creatable_uuid',
    'contact_source_updated_at',
]

// Format value for display
const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '—'
    if (value === '') return '(trống)'
    if (typeof value === 'boolean') return value ? 'Có' : 'Không'
    if (typeof value === 'number') return String(value)
    if (typeof value === 'string') return value
    if (Array.isArray(value)) {
        if (value.length === 0) return '(trống)'
        // Try to extract meaningful values from array items
        return value
            .map((v) => {
                if (typeof v === 'object' && v !== null) {
                    return v.name || v.title || v.label || formatValue(v)
                }
                return formatValue(v)
            })
            .join(', ')
    }
    if (typeof value === 'object') {
        // Try to find a meaningful display value
        const displayKeys = ['name', 'title', 'label', 'email', 'value']
        for (const key of displayKeys) {
            if (value[key]) return String(value[key])
        }
        // Return count of keys
        const keys = Object.keys(value).filter((k) => !UnpickKey.includes(k))
        if (keys.length === 0) return '(trống)'
        return `${keys.length} trường`
    }
    return String(value)
}

// Single change item - inline style
const ChangeItem: React.FC<{
    label: string
    oldValue: any
    newValue: any
}> = ({ label, oldValue, newValue }) => {
    const { t } = useTranslation()

    const oldDisplay = formatValue(oldValue)
    const newDisplay = formatValue(newValue)

    return (
        <div className="flex items-center gap-2 flex-wrap py-0.5">
            <span className="text-xs text-gray-500 min-w-[80px]">
                {t(label)}:
            </span>
            <span className="text-xs px-2 py-0.5 bg-red-50 text-red-600 rounded line-through max-w-[150px] truncate">
                {oldDisplay}
            </span>
            <span className="text-gray-400">→</span>
            <span className="text-xs px-2 py-0.5 bg-green-50 text-green-600 rounded max-w-[150px] truncate">
                {newDisplay}
            </span>
        </div>
    )
}

// Metadata display (non-diff mode)
const MetadataItem: React.FC<{ label: string; value: any }> = ({
    label,
    value,
}) => {
    const { t } = useTranslation()
    const display = formatValue(value)

    return (
        <div className="flex items-center gap-2 py-1.5">
            <span className="text-xs text-gray-500 min-w-[80px]">
                {t(label)}:
            </span>
            <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded max-w-[200px] truncate">
                {display}
            </span>
        </div>
    )
}

const Changelogs: React.FC<MetadataProps> = ({ metadata }) => {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)

    const hasOldAndNew = metadata?.old && metadata?.new

    if (hasOldAndNew) {
        // Helper to check if values are equal
        const isEqual = (oldVal: any, newVal: any): boolean => {
            if (oldVal === newVal) return true
            if (oldVal == null || newVal == null) return false

            const isEmptyObj = (v: any) =>
                typeof v === 'object' &&
                !Array.isArray(v) &&
                Object.keys(v).length === 0
            if (
                isEmptyObj(oldVal) &&
                (newVal === false || newVal === '' || newVal === 0)
            )
                return true
            if (
                isEmptyObj(newVal) &&
                (oldVal === false || oldVal === '' || oldVal === 0)
            )
                return true

            if (typeof oldVal !== typeof newVal) return false
            if (typeof oldVal !== 'object') return oldVal === newVal

            return JSON.stringify(oldVal) === JSON.stringify(newVal)
        }

        // Filter to only show changed fields
        const keyChanges = Object.keys({ ...(metadata?.old ?? {}) })
            .filter((key) => !UnpickKey.includes(key))
            .filter(
                (key) => !isEqual(metadata?.old?.[key], metadata?.new?.[key])
            )

        if (keyChanges.length === 0) {
            return null
        }

        const visibleChanges = isOpen ? keyChanges : keyChanges.slice(0, 2)
        const hiddenCount = keyChanges.length - 2

        return (
            <div className="mt-2 ps-8">
                {visibleChanges.map((key) => (
                    <ChangeItem
                        key={key}
                        label={key}
                        oldValue={metadata?.old?.[key]}
                        newValue={metadata?.new?.[key]}
                    />
                ))}
                {hiddenCount > 0 && (
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 py-1"
                    >
                        <EyeIcons className="w-3 h-3" />
                        {isOpen
                            ? t('collapse')
                            : `+${hiddenCount} thay đổi khác`}
                    </button>
                )}
            </div>
        )
    }

    // Non-diff mode: just display metadata
    const allKeys = Object.keys(metadata || {}).filter(
        (key) => !UnpickKey.includes(key)
    )

    if (allKeys.length === 0) return null

    const visibleKeys = isOpen ? allKeys : allKeys.slice(0, 2)
    const hiddenCount = allKeys.length - 2

    return (
        <div className="mt-2 pl-2 border-l-2 border-gray-200 ps-8">
            {visibleKeys.map((key) => (
                <MetadataItem key={key} label={key} value={metadata?.[key]} />
            ))}
            {hiddenCount > 0 && (
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 py-1"
                >
                    <EyeIcons className="w-3 h-3" />
                    {isOpen ? t('collapse') : `+${hiddenCount} trường khác`}
                </button>
            )}
        </div>
    )
}

export default Changelogs
